import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Publication } from '../../../mocks/publications';

const typeColors: Record<string, string> = {
  'Book': 'bg-amber-100 text-amber-800',
  'Journal Article': 'bg-emerald-100 text-emerald-800',
  'Magazine Publication': 'bg-rose-100 text-rose-800',
  'Gazette': 'bg-violet-100 text-violet-800',
  'Essay': 'bg-sky-100 text-sky-800',
  'Catalogue': 'bg-orange-100 text-orange-800',
  'Curatorial Publication': 'bg-teal-100 text-teal-800',
};

interface PublicationCardProps {
  publication: Publication;
  style?: React.CSSProperties;
  className?: string;
}

interface LightboxProps {
  images: string[];
  initialIndex: number;
  title: string;
  onClose: () => void;
}

function Lightbox({ images, initialIndex, title, onClose }: LightboxProps) {
  const [current, setCurrent] = useState(initialIndex);

  const prev = useCallback(() => setCurrent((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setCurrent((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer"
        onClick={onClose}
      >
        <i className="ri-close-line text-xl" />
      </button>

      {/* Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-sans">
          {current + 1} / {images.length}
        </div>
      )}

      {/* Prev arrow */}
      {images.length > 1 && (
        <button
          className="absolute left-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
          onClick={(e) => { e.stopPropagation(); prev(); }}
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
      )}

      {/* Image */}
      <div
        className="max-w-3xl max-h-[85vh] flex items-center justify-center px-16"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[current]}
          alt={`${title} image ${current + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl"
        />
      </div>

      {/* Next arrow */}
      {images.length > 1 && (
        <button
          className="absolute right-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
          onClick={(e) => { e.stopPropagation(); next(); }}
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
              className={`w-12 h-16 rounded overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${i === current ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-75'}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicationCard({ publication, style, className = '' }: PublicationCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const lightboxImages = [
    ...(publication.coverImage ? [publication.coverImage] : []),
    ...(publication.additionalImages ?? []),
  ];

  const hasMultipleImages = lightboxImages.length > 1;

  const schemaType = publication.type === 'Book' ? 'https://schema.org/Book'
    : publication.type === 'Journal Article' ? 'https://schema.org/ScholarlyArticle'
    : 'https://schema.org/CreativeWork';

  return (
    <>
      <article
        style={style}
        itemScope
        itemType={schemaType}
        className={`bg-white rounded-lg overflow-hidden flex flex-col group hover:bg-stone-50 transition-colors duration-300 border border-stone-100 self-start ${className}`}
      >
        {/* Hidden machine-readable author */}
        <span itemProp="author" itemScope itemType="https://schema.org/Person" className="hidden">
          <span itemProp="name">Oludamola Adebowale</span>
          <link itemProp="url" href="https://oludamolaadebowale.com/" />
        </span>
        <meta itemProp="datePublished" content={publication.year} />
        {publication.isbn && <meta itemProp="isbn" content={publication.isbn} />}
        {publication.pages && <meta itemProp="numberOfPages" content={publication.pages} />}
        <meta itemProp="inLanguage" content="en" />

        {/* Cover image */}
        {publication.coverImage && (
          <div
            className="w-full bg-stone-100 flex-shrink-0 relative cursor-pointer overflow-hidden"
            onClick={() => setLightboxOpen(true)}
          >
            <img
              src={publication.coverImage}
              alt={publication.title}
              itemProp="image"
              className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-500"
            />
            {/* View indicator */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-sans text-stone-700">
                <i className="ri-zoom-in-line text-sm" />
                {hasMultipleImages ? `View ${lightboxImages.length} images` : 'View image'}
              </div>
            </div>
          </div>
        )}

        <div className="p-6 flex flex-col">
          {/* Type badge + year */}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeColors[publication.type] ?? 'bg-gray-100 text-gray-700'}`}>
              {publication.type}
            </span>
            <span className="text-xs text-gray-400 font-sans">{publication.year}</span>
          </div>

          {/* Title — never clickable; buy buttons handle the link */}
          <h3 itemProp="name" className="font-serif text-lg font-semibold text-gray-900 mb-2 leading-snug">
            {publication.title}
          </h3>

          {/* Abstract */}
          <p itemProp="description" className="font-sans text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">
            {publication.abstract}
          </p>

          {/* Meta */}
          <div className="flex flex-wrap gap-3 mt-auto pt-4 border-t border-stone-100">
            {publication.pages && (
              <span className="flex items-center gap-1 text-xs text-gray-400 font-sans">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-file-text-line text-xs" />
                </div>
                {publication.pages} pp.
              </span>
            )}
            {publication.isbn && (
              <span className="flex items-center gap-1 text-xs text-gray-400 font-sans">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-barcode-line text-xs" />
                </div>
                ISBN
              </span>
            )}
            {publication.doi && (
              <span className="flex items-center gap-1 text-xs text-gray-400 font-sans">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-links-line text-xs" />
                </div>
                DOI
              </span>
            )}
            {hasMultipleImages && (
              <button
                onClick={() => setLightboxOpen(true)}
                className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 font-sans cursor-pointer transition-colors ml-auto whitespace-nowrap"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-image-2-line text-xs" />
                </div>
                {lightboxImages.length} images
              </button>
            )}
          </div>

          {/* Topics */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {publication.topic.map((t) => (
              <span key={t} itemProp="keywords" className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full font-sans">
                {t}
              </span>
            ))}
          </div>

          {/* View detail link */}
          <Link
            to={`/publications/${publication.id}`}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-sans font-medium text-gray-700 hover:text-[#b89a6a] transition-colors cursor-pointer"
          >
            View details
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs" />
            </div>
          </Link>

          {/* Buy Now button — for books with a direct purchase URL */}
          {(publication.amazonUrl || publication.buyNowUrl) && (
            <a
              href={publication.amazonUrl ?? publication.buyNowUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 font-sans text-xs font-semibold bg-gray-900 text-white px-4 py-2.5 rounded-md hover:bg-[#b89a6a] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-xs" />
              </div>
              Buy Now
            </a>
          )}

          {/* Request a Copy button — navigates to Press & Enquiries page */}
          {publication.requestCopy && (
            <Link
              to="/press#contact"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 font-sans text-xs font-semibold bg-[#f9f6f1] text-gray-800 border border-stone-200 px-4 py-2.5 rounded-md hover:bg-[#b89a6a] hover:text-white hover:border-[#b89a6a] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-mail-line text-xs" />
              </div>
              Request a Copy
            </Link>
          )}
        </div>
      </article>

      {lightboxOpen && lightboxImages.length > 0 && (
        <Lightbox
          images={lightboxImages}
          initialIndex={0}
          title={publication.title}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}



